import React, { useState, useCallback } from 'react';
import CarCard from '../components/CarCard';
import CarModelViewer from '../components/CarModelViewer';
import ModelLoader from '../components/ui/ModelLoader';
import { useCars } from '../context/CarsContext';
import './Home.css';

const Home: React.FC = () => {
  const { cars } = useCars();
  const [filter, setFilter] = useState<'all' | 'available' | 'sold'>('all');
  const [search, setSearch] = useState('');
  const [loadProgress, setLoadProgress] = useState(0);
  const [modelReady, setModelReady] = useState(false);

  const filteredCars = cars
    .filter(car => {
      const matchesFilter = filter === 'all' || car.status === filter;
      const matchesSearch = search.trim() === '' ||
        car.title.toLowerCase().includes(search.toLowerCase()) ||
        car.features.some(f => f.toLowerCase().includes(search.toLowerCase()));
      return matchesFilter && matchesSearch;
    })
    .sort((a, b) => {
      if (a.status === b.status) return 0;
      return a.status === 'available' ? -1 : 1;
    });

  const availableCars = cars.filter(car => car.status === 'available');
  const soldCars = cars.filter(car => car.status === 'sold');

  const handleProgress = useCallback((p: number) => setLoadProgress(p), []);
  const handleReady = useCallback(() => setModelReady(true), []);

  return (
    <>
      <ModelLoader progress={loadProgress} ready={modelReady} />

      <div
        className="home"
        style={{
          opacity: modelReady ? 1 : 0,
          transition: 'opacity 0.8s cubic-bezier(0.4, 0, 0.2, 1)',
          transitionDelay: modelReady ? '0.1s' : '0s',
        }}
      >
        {/* Hero */}
        <section className="hero">
          <div className="hero__content">
            <h1 className="hero__title">
              Rare Imports,<br />
              <span className="hero__title-accent">Delivered.</span>
            </h1>
            <p className="hero__body">
              Hand-picked European and Japanese performance vehicles,
              competitively priced.
            </p>
            <div className="hero__actions">
              <a href="#inventory" className="btn btn-primary">Browse Inventory</a>
              <a href="/contact" className="btn btn-secondary">Get in Touch</a>
            </div>
          </div>

          <div className="hero__spline" aria-hidden="true">
            <CarModelViewer onProgress={handleProgress} onReady={handleReady} />
          </div>
        </section>

        {/* Inventory */}
        <section className="section inventory-section" id="inventory">
          <div className="container">
            <div className="search-bar-wrapper">
              <input
                type="text"
                className="public-search"
                placeholder="Search by title or feature..."
                value={search}
                onChange={e => setSearch(e.target.value)}
              />
            </div>

            <div className="inventory-filters">
              <button
                className={`filter-btn ${filter === 'all' ? 'active' : ''}`}
                onClick={() => setFilter('all')}
              >
                All ({cars.length})
              </button>
              <button
                className={`filter-btn ${filter === 'available' ? 'active' : ''}`}
                onClick={() => setFilter('available')}
              >
                Available ({availableCars.length})
              </button>
              <button
                className={`filter-btn ${filter === 'sold' ? 'active' : ''}`}
                onClick={() => setFilter('sold')}
              >
                Sold ({soldCars.length})
              </button>
            </div>

            <div className="cars-grid">
              {filteredCars.map((car) => (
                <CarCard key={car.id} car={car} />
              ))}
            </div>
          </div>
        </section>
      </div>
    </>
  );
};

export default Home;
